import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    if (user) {
      navigate("/");
    } else {
      navigate("/onboarding");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[680px] mx-auto px-5 py-3 flex items-center justify-between">
          <span
            className="text-base font-black tracking-tight text-slate-800 cursor-pointer"
            onClick={() => navigate("/")}
          >
            Birthday<span className="text-amber-500">DeaLight</span>
          </span>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            Back
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-[680px] mx-auto px-5 py-10 sm:py-14">
        <h1
          className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight mb-8"
          data-testid="about-title"
        >
          What started as a spreadsheet for one.
        </h1>

        <div className="space-y-5 text-slate-700 text-lg leading-relaxed" data-testid="about-body">
          <p>
            What started as a spreadsheet for one, eight years ago, is now becoming something for everyone.
          </p>
          <p>
            A simple idea: nobody should miss out on a birthday deal because the info was wrong, buried, or impossible to find. So we started collecting. One deal at a time. One row at a time. Now we're building the most accurate birthday rewards database in the country — and it gets smarter with every person who joins.
          </p>
          <p>
            Most people walk right past hundreds of dollars in free birthday rewards every single year — free meals, free desserts, free drinks, free beauty products — and never even know they exist. Not anymore.
          </p>
          <p>
            We've done the legwork. Hundreds of deals, verified and organized, ready for your big day. But we haven't captured it all. We're still building, still adding, still improving. And the truth is — deals are living things. They change, they improve, they sometimes quietly disappear. That's where this becomes bigger than an app.
          </p>
          <p className="text-xl font-bold text-slate-900">
            That's where you come in.
          </p>
          <p>
            Know a place we missed? A mom-and-pop spot with an incredible birthday deal? A hole-in-the-wall that only the neighbors know about? A hidden gem that doesn't advertise or even have a website? These are the golden finds that deserve to be brought into the light — for others to explore, discover, and celebrate at. And it all starts with you sharing what you know.
          </p>
          <p>
            Wouldn't you love to discover a hidden gem in your own neighborhood that you never knew about? Someone just like you made that possible.
          </p>
          <p>
            When you verify a deal, flag a change, or share a find with a friend, you're not just helping yourself — you're helping the next person who walks through that door on their birthday. Strangers and friends, coming together for one goal. What you do today helps someone else tomorrow, and comes back around to you on your next birthday.
          </p>
          <p>
            We're just getting started. Right now, we're launching in select areas across California — a small market test to get things right. Every week, we're adding more deals, more cities, and more national brand names. How fast we grow depends on you and this community. The more people who join, verify, and share, the faster this reaches every city, every neighborhood, every hidden gem in the country.
          </p>
          <p>
            So here's the deal: we promise to keep building, keep improving, and keep fighting for your best birthday ever. All we ask? If you see something off, let us know. If a deal works great, confirm it. If you love a find, share it with someone whose birthday is coming up. That's not just a link — that's a gift.
          </p>
          <p>
            The more of us who pitch in, the better this gets for everyone. Your input today becomes someone else's perfect birthday tomorrow.
          </p>
          <p className="text-xl font-bold text-slate-900">
            We started this. You make it unstoppable.
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={handleCTA}
          className="mt-10 w-full py-4 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-black text-base shadow-lg shadow-amber-500/20 transition"
          data-testid="about-cta"
        >
          Find Your BirthdayDeaLight
        </button>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 bg-slate-50">
        <div className="max-w-[680px] mx-auto px-5">
          <p className="text-xs text-slate-400 text-center mb-3">
            Birthday<span className="text-amber-500 font-bold">DeaLight</span>
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
            <a href="/privacy" className="hover:text-slate-600">Privacy Policy</a>
            <span className="text-slate-200">·</span>
            <a href="/terms" className="hover:text-slate-600">Terms of Service</a>
            <span className="text-slate-200">·</span>
            <a href="/about" className="hover:text-slate-600 font-medium text-slate-500">About</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
